import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useTranslation } from '@kgc/i18n';
import { Check, Plus, ShoppingCart, Truck, X } from 'lucide-react';
import * as React from 'react';

/**
 * PartnerRoleToggleChips — STORY-1212 (B-13) Partner Detail header chip row
 * for dual-role toggle (Sally 03-partner §2.2, ADR-124 D-3).
 *
 * Scope (Phase 1 UI scaffold):
 *  - Displays current roles as "✓ VEVŐ" / "✓ BESZÁLLÍTÓ" chips
 *  - Adds "+ {ROLE}-t hozzáad" button for missing roles
 *  - On toggle, calls `onToggle` with pending role + confirm-or-cancel result
 *  - UI ONLY — Partner.role enum backend migration is a follow-up story
 *    (separate ADR-124 D-3 schema/service story). Parent is responsible for
 *    audit-log + API call; this component is presentation + confirm flow.
 *
 * Q1 gate-7 decision (2026-04-24): confirm dialog + rollback capability.
 * B-13 Blind Hunter P0 #2 fix — debounce rewritten to leading-edge:
 *  - Confirm dialog opens IMMEDIATELY on click (no laggy 500ms wait)
 *  - Guard against re-entry: `if (pendingRole) return` — ignores new clicks
 *    while confirm is already open (handles Edge Hunter P1 #1 rapid toggle)
 *  - 500ms minimum inter-click interval prevents "double-click confirms twice"
 *    race where the user mashes the button.
 *
 * B-13 Blind Hunter P1 #1 fix — confirm modal uses shadcn `Dialog` primitive
 * (Radix): portaled to document.body, traps focus, closes on Escape.
 *
 * B-13 Edge Case Hunter P1 #2 fix — if `currentRoles` changes such that the
 * pending toggle action is no longer valid (e.g. CUSTOMER already added via
 * another flow while the confirm was open), the dialog auto-closes.
 */

export type PartnerRoleFlag = 'CUSTOMER' | 'SUPPLIER';

export interface PartnerRoleToggleChipsProps {
  /** Current partner roles. Empty array = neither role set (new partner). */
  currentRoles: PartnerRoleFlag[];
  /**
   * Toggle callback. Caller performs:
   *  - API PATCH to partner.role enum
   *  - Audit-log event
   *  - Undo-toast rendering (rollback affordance)
   *
   * Promise resolution signals success (chip re-rendered with new state);
   * throw = toggle rejected (chip stays as-is, component surfaces error).
   */
  onToggle: (role: PartnerRoleFlag, action: 'add' | 'remove') => Promise<void> | void;
  /**
   * Loading override — when true, both buttons are disabled (parent is mid-
   * update via another flow, e.g. status edit).
   */
  disabled?: boolean;
  /**
   * Read-only ok (tooltip) ha `disabled` — pl. a deferred Partner.role enum
   * migráció (STORY-1212 AC-2). Ha megadva, a chip-konténer `title`+`aria-label`-t
   * kap, így a demón a pultos tooltipet lát (NEM JS-hibát, mint a régi throw).
   */
  disabledReason?: string;
}

const MIN_INTER_CLICK_MS = 500;

export function PartnerRoleToggleChips({
  currentRoles,
  onToggle,
  disabled = false,
  disabledReason,
}: PartnerRoleToggleChipsProps) {
  const { t } = useTranslation();

  const [pendingRole, setPendingRole] = React.useState<{
    role: PartnerRoleFlag;
    action: 'add' | 'remove';
  } | null>(null);
  const [inFlight, setInFlight] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const lastClickAtRef = React.useRef(0);
  const isMountedRef = React.useRef(true);
  React.useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const hasCustomer = currentRoles.includes('CUSTOMER');
  const hasSupplier = currentRoles.includes('SUPPLIER');

  // B-13 Edge P1 #2 — close stale confirm if external currentRoles drift
  // makes the pending action a no-op (e.g. ADD CUSTOMER pending but
  // currentRoles got CUSTOMER elsewhere).
  React.useEffect(() => {
    if (!pendingRole) return;
    const has = pendingRole.role === 'CUSTOMER' ? hasCustomer : hasSupplier;
    const staleAdd = pendingRole.action === 'add' && has;
    const staleRemove = pendingRole.action === 'remove' && !has;
    if (staleAdd || staleRemove) {
      setPendingRole(null);
      setError(null);
    }
  }, [hasCustomer, hasSupplier, pendingRole]);

  const openConfirm = React.useCallback(
    (role: PartnerRoleFlag, action: 'add' | 'remove') => {
      if (disabled || inFlight) return;
      // B-13 Blind P0 #2 — leading-edge debounce: only guard against rapid
      // double-click. First click opens the dialog immediately.
      const now = Date.now();
      if (now - lastClickAtRef.current < MIN_INTER_CLICK_MS) return;
      lastClickAtRef.current = now;
      // Edge P1 #1 — if a confirm is already open, ignore new clicks until
      // the user decides (cancel closes it; confirm proceeds). This matches
      // the modal-confirm semantics (can't have two confirms open at once).
      if (pendingRole) return;
      setPendingRole({ role, action });
      setError(null);
    },
    [disabled, inFlight, pendingRole]
  );

  const handleConfirm = React.useCallback(async () => {
    if (!pendingRole) return;
    setInFlight(true);
    setError(null);
    try {
      await onToggle(pendingRole.role, pendingRole.action);
      if (isMountedRef.current) setPendingRole(null);
    } catch (ex) {
      const msg = ex instanceof Error ? ex.message : String(ex);
      if (isMountedRef.current) setError(msg);
    } finally {
      if (isMountedRef.current) setInFlight(false);
    }
  }, [pendingRole, onToggle]);

  const handleCancel = React.useCallback(() => {
    if (inFlight) return;
    setPendingRole(null);
    setError(null);
  }, [inFlight]);

  const handleOpenChange = React.useCallback(
    (next: boolean) => {
      if (next) return; // only care about close
      handleCancel();
    },
    [handleCancel]
  );

  const renderChip = (role: PartnerRoleFlag, hasRole: boolean) => {
    const Icon = role === 'CUSTOMER' ? ShoppingCart : Truck;
    const labelKey =
      role === 'CUSTOMER' ? 'partner.roleToggle.customerLabel' : 'partner.roleToggle.supplierLabel';
    const addLabelKey =
      role === 'CUSTOMER' ? 'partner.roleToggle.addCustomer' : 'partner.roleToggle.addSupplier';

    if (hasRole) {
      return (
        <span
          className="inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200"
          data-testid={`partner-role-chip-${role.toLowerCase()}`}
        >
          <Check className="h-3 w-3" aria-hidden="true" />
          <Icon className="h-3 w-3" aria-hidden="true" />
          {t(labelKey)}
          <button
            type="button"
            onClick={() => openConfirm(role, 'remove')}
            disabled={disabled || inFlight}
            className="ml-1 inline-flex items-center rounded-full p-0.5 hover:bg-emerald-200 dark:hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-50"
            aria-label={t('partner.roleToggle.removeAria')}
            data-testid={`partner-role-remove-${role.toLowerCase()}`}
          >
            <X className="h-3 w-3" />
          </button>
        </span>
      );
    }
    return (
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => openConfirm(role, 'add')}
        disabled={disabled || inFlight}
        data-testid={`partner-role-add-${role.toLowerCase()}`}
        className="h-7 text-xs"
      >
        <Plus className="mr-1 h-3 w-3" aria-hidden="true" />
        <Icon className="mr-1 h-3 w-3" aria-hidden="true" />
        {t(addLabelKey)}
      </Button>
    );
  };

  return (
    <div
      className="flex flex-wrap items-center gap-2"
      data-testid="partner-role-toggle-chips"
      {...(disabled && disabledReason
        ? { title: disabledReason, 'aria-label': disabledReason }
        : {})}
    >
      {renderChip('CUSTOMER', hasCustomer)}
      {renderChip('SUPPLIER', hasSupplier)}

      <Dialog open={pendingRole !== null} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-sm" data-testid="partner-role-toggle-confirm">
          <DialogHeader>
            <DialogTitle>
              {pendingRole?.action === 'add'
                ? t('partner.roleToggle.confirmAddTitle')
                : t('partner.roleToggle.confirmRemoveTitle')}
            </DialogTitle>
            <DialogDescription>
              {pendingRole
                ? t(
                    pendingRole.action === 'add'
                      ? 'partner.roleToggle.confirmAddText'
                      : 'partner.roleToggle.confirmRemoveText',
                    { role: pendingRole.role }
                  )
                : ''}
            </DialogDescription>
          </DialogHeader>
          {error && (
            <p
              className="text-xs text-red-700 dark:text-red-300"
              data-testid="partner-role-toggle-error"
            >
              {error}
            </p>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleCancel}
              disabled={inFlight}
              data-testid="partner-role-toggle-cancel"
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={() => void handleConfirm()}
              disabled={inFlight}
              data-testid="partner-role-toggle-confirm-btn"
            >
              {inFlight ? t('common.saving') : t('common.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
